import { Injectable, inject } from '@angular/core';
import { extractDatasetResourceDisplayTags } from '@angular-workspace/feature';
import { FeatureCardData } from '../components/feature-card/feature-card.component';
import { FormatService } from './format.service';
import { HelperService } from './helper.service';

/** Maps catalogue API dataset items to {@link FeatureCardData} for consistent dataset cards across the app. */
@Injectable({ providedIn: 'root' })
export class FeatureCardMapperService {
  private readonly formatService = inject(FormatService);
  private readonly helperService = inject(HelperService);

  mapApiItemToFeatureCard(item: any): FeatureCardData {
    const id = item?.id || item?.['@id'] || '';
    const title = item?.label || item?.name || 'Untitled';
    const organization =
      item?.organizationName ||
      item?.provider ||
      item?.organisation ||
      item?.organization ||
      item?.provider_name ||
      '';
    const rawDesc =
      item?.shortDescription ??
      item?.short_description ??
      item?.sortDescription ??
      item?.description ??
      '';
    const shortDescription =
      typeof rawDesc === 'string' && rawDesc.trim()
        ? rawDesc.length > 120
          ? `${rawDesc.trim().slice(0, 120)}…`
          : rawDesc.trim()
        : undefined;

    const { resourceServerNames, queryTypes } = extractDatasetResourceDisplayTags(item);

    const apiTags: string[] = Array.isArray(item?.tags) ? [...item.tags] : [];
    const percentage = item?.percentage || item?.dataReadiness || undefined;
    const fileFormat = item?.fileFormat || item?.format || '';
    const fileType = fileFormat || '';
    const date = this.formatService.formatDateForCard(
      item?.itemCreatedAt || item?.createdAt || item?.created_at || new Date(),
    );
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);
    const isNew = this.isRecentItem(item?.itemCreatedAt || item?.createdAt || item?.created_at);
    const metricsData = item?.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item?.likeCount ?? item?.likes ?? item?.like_count ?? 0,
      downloads:
        metricsData.downloads ?? item?.downloadCount ?? item?.downloads ?? item?.download_count ?? 0,
      files: item?.fileCount || item?.files || item?.file_count || 0,
      views: metricsData.views ?? item?.viewCount ?? item?.views ?? item?.view_count ?? 0,
      dislikes: metricsData.dislikes ?? 0,
    };

    return {
      id,
      title,
      shortDescription,
      organization,
      industry: this.helperService.extractIndustry(item),
      tags: resourceServerNames.slice(0, 3),
      serverAccessTypes: queryTypes.length > 0 ? queryTypes : undefined,
      apiTags,
      percentage,
      fileType,
      date,
      badge,
      badgeColor,
      isNew,
      link: `/dataset/${id}`,
      metrics,
    };
  }

  private isRecentItem(dateString: string | Date | undefined): boolean {
    if (!dateString) {
      return false;
    }
    const itemDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return itemDate >= weekAgo;
  }
}
